import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Comprehensive ResizeObserver error suppression
// This is a known issue with react-bootstrap-typeahead and ResizeObserver API
const suppressResizeObserverErrors = () => {
  // Suppress console errors
  const originalConsoleError = console.error;
  console.error = function(...args) {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
      return; // Suppress this specific error
    }
    originalConsoleError.apply(console, args);
  };

  // Handle window error events
  window.addEventListener('error', function(event) {
    if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.preventDefault();
      return false;
    }
  });

  // Handle unhandled promise rejections that might contain ResizeObserver errors
  window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.preventDefault();
      return false;
    }
    
    // Log other unhandled promise rejections for debugging
    console.error('Unhandled promise rejection:', event.reason);
    
    // Prevent the default browser behavior for unhandled rejections
    event.preventDefault();
  });

  // More aggressive ResizeObserver override with debouncing
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        let timeoutId;
        super((entries, observer) => {
          // Clear any existing timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // Debounce the callback to prevent rapid fire
          timeoutId = setTimeout(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              // Silently ignore all ResizeObserver errors
              if (error.message && (
                error.message.includes('ResizeObserver loop completed') ||
                error.message.includes('ResizeObserver loop limit exceeded')
              )) {
                return;
              }
              // Re-throw other errors
              throw error;
            }
          }, 16); // ~60fps
        });
      }
    };
  }

  // Patch Error constructor to catch ResizeObserver errors.
  // Must tolerate non-string messages (e.g. Primer SDK passes objects).
  const OriginalError = window.Error;
  const getErrorMessageText = (message) => {
    if (typeof message === 'string') {
      return message;
    }
    if (message && typeof message.message === 'string') {
      return message.message;
    }
    return '';
  };
  window.Error = function Error(message, ...args) {
    if (getErrorMessageText(message).includes('ResizeObserver loop completed with undelivered notifications')) {
      return { message: '', stack: '', toString: () => '' };
    }
    return new OriginalError(message, ...args);
  };
  window.Error.prototype = OriginalError.prototype;
};

// Apply the error suppression
suppressResizeObserverErrors();

// Additional webpack/React error boundary suppression
// This targets the specific bundle.js error handling that shows up in development
if (process.env.NODE_ENV === 'development') {
  // Patch the global error handler that webpack uses
  const originalOnerror = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && message.includes('ResizeObserver loop completed with undelivered notifications')) {
      return true; // Prevent default error handling
    }
    if (originalOnerror) {
      return originalOnerror.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // Patch webpack error handler
  const originalErrorHandler = window.__webpack_require__ && window.__webpack_require__.oe;
  if (originalErrorHandler) {
    window.__webpack_require__.oe = function(error) {
      if (error && error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      return originalErrorHandler.call(this, error);
    };
  }

  // Intercept React's error overlay
  const interceptReactErrorOverlay = () => {
    if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
      const originalErrorHandler = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError;
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError = function(error) {
        if (error && error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
          return; // Suppress this specific error from the overlay
        }
        if (originalErrorHandler) {
          return originalErrorHandler.call(this, error);
        }
      };
    } else {
      // Try again after a short delay if the hook isn't available yet
      setTimeout(interceptReactErrorOverlay, 100);
    }
  };
  interceptReactErrorOverlay();

  // Patch the runtime error handler that appears in the browser
  setTimeout(() => {
    if (window.__webpack_require__ && window.__webpack_require__.cache) {
      const modules = window.__webpack_require__.cache;
      Object.keys(modules).forEach(moduleId => {
        const module = modules[moduleId];
        if (module && module.exports && typeof module.exports.handleError === 'function') {
          const originalHandleError = module.exports.handleError;
          module.exports.handleError = function(error) {
            if (error && error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
              return; // Suppress this specific error
            }
            return originalHandleError.call(this, error);
          };
        }
      });
    }
  }, 1000);
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
